import {
  DocumentIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  TrashIcon,
  ChevronLeftIcon,
} from "@heroicons/react/24/outline";
import Header from "../components/layout/Header";
import { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { directMessagingService } from "../services/directMessagingService";
import { DMConversation, Message } from "../types/directMessages";
import { formatRelativeTime } from "../utils/timeUtils";
import AttachmentModal from "../components/messages/AttachmentModal";

import BottomNavBar from "../components/layout/BottomNav";

const InboxPage = () => {
  const { currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [messageInput, setMessageInput] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [conversations, setConversations] = useState<DMConversation[]>([]);
  const [selectedConversation, setSelectedConversation] =
    useState<DMConversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<{
    conversationId: string;
    messageId: string;
  } | null>(null);

  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Subscribe to real-time conversations for the current user
  useEffect(() => {
    if (!currentUser?.uid) return;

    // Subscribe to real-time conversations
    const unsubscribe = directMessagingService.subscribeToConversations(
      currentUser.uid,
      (userConversations) => {
        setConversations(userConversations);

        // Auto-select first conversation only on desktop
        if (
          userConversations.length > 0 &&
          !selectedConversation &&
          !isMobile
        ) {
          setSelectedConversation(userConversations[0]);
        }
      },
    );

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [currentUser?.uid, isMobile]);

  // Subscribe to real-time messages when selectedConversation changes
  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      return;
    }

    // Don't subscribe for temporary conversations
    if (selectedConversation.id.startsWith("temp_")) {
      setMessages([]);
      return;
    }

    // Subscribe to real-time messages
    const unsubscribe = directMessagingService.subscribeToMessages(
      selectedConversation.id,
      (newMessages) => {
        setMessages(newMessages);
      },
    );

    // Cleanup subscription on unmount or when conversation changes
    return () => {
      unsubscribe();
    };
  }, [selectedConversation?.id]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!selectedConversation || !currentUser || !messageInput.trim()) return;

    setSendingMessage(true);

    if (selectedConversation.id.startsWith("temp_")) {
      // Handle sending message in a new conversation
      const conversationData: Partial<DMConversation> = {
        participantIds: selectedConversation.participantIds,
      };

      const messageData = {
        senderUid: currentUser.uid,
        message: messageInput,
        type: "text" as const,
      };

      try {
        const newConversation = await directMessagingService.createConversation(
          conversationData,
          messageData,
        );
        setMessageInput("");
        // Refetch conversations to get updated list
        // Set the newly created conversation as selected
        setSelectedConversation(newConversation);
        // Fetch messages for the new conversation
        const conversationMessages = await directMessagingService.fetchMessages(
          newConversation.id,
        );
        setMessages(conversationMessages);
      } catch (error) {
        console.error(
          "Error creating conversation and sending message:",
          error,
        );
      } finally {
        setSendingMessage(false);
      }
    } else {
      // Handle sending message in existing conversation
      const messageData = {
        senderUid: currentUser.uid,
        message: messageInput,
        type: "text" as const,
        readBy: [currentUser.uid],
      };

      try {
        await directMessagingService.sendMessage(
          selectedConversation.id,
          messageData,
        );
        setMessageInput("");
        // Refetch conversations to get updated list with new message
        // Refetch messages to show the new message
        const conversationMessages = await directMessagingService.fetchMessages(
          selectedConversation.id,
        );
        setMessages(conversationMessages);
      } catch (error) {
        console.error("Error sending message:", error);
      } finally {
        setSendingMessage(false);
      }
    }
  };

  const handleDeleteMessage = async (
    conversationId: string,
    messageId: string,
  ) => {
    try {
      await directMessagingService.deleteMessage(conversationId, messageId);
      // Refetch messages after deletion
      if (selectedConversation) {
        const conversationMessages = await directMessagingService.fetchMessages(
          selectedConversation.id,
        );
        setMessages(conversationMessages);
      }
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  };

  const handleDeleteClick = (conversationId: string, messageId: string) => {
    setMessageToDelete({ conversationId, messageId });
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteMessage = async () => {
    if (messageToDelete) {
      await handleDeleteMessage(
        messageToDelete.conversationId,
        messageToDelete.messageId,
      );
    }
    setShowDeleteConfirmation(false);
    setMessageToDelete(null);
  };

  const cancelDeleteMessage = () => {
    setShowDeleteConfirmation(false);
    setMessageToDelete(null);
  };

  const handleSendAttachment = async (file: File) => {
    if (!selectedConversation || !currentUser) return;

    try {
      await directMessagingService.sendAttachment(
        selectedConversation.id,
        file,
        currentUser.uid,
      );
      // Real-time listener will automatically update the messages
    } catch (error) {
      console.error("Error sending attachment:", error);
      throw error;
    }
  };

  // Handle back button on mobile
  const handleBackToList = () => {
    setSelectedConversation(null);
  };

  return (
    <div
      className={`w-full h-screen flex flex-col bg-base-200 overflow-hidden pt-[64px] md:pt-[72px]`}
    >
      <Header maxWidth="w-full" />

      <div
        className={`max-w-5xl mx-auto w-full max-h-[90vh] overflow-y-auto ${
          isMobile && selectedConversation ? "hidden" : ""
        }`}
      >
        {/* Header */}
        <div className="py-8 px-8">
          <h2 className="text-3xl font-bold text-base-content font-heading">
            Inbox
          </h2>
        </div>
      </div>
      <div className="flex justify-center flex-1 pt-0 md:p-2">
        <div className="flex gap-2 w-full max-w-5xl">
          {/* Sidebar - Hidden on mobile when conversation is selected */}
          <div
            className={`w-full md:max-w-md bg-base-100 shadow-md md:rounded-md flex flex-col h-full overflow-hidden ${
              isMobile && selectedConversation ? "hidden" : ""
            }`}
          >
            <div className="flex flex-col gap-1 p-8 flex-shrink-0">
              {/* Search Bar */}
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 text-base-content/60 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by user..."
                  className="w-full bg-base-300 placeholder:font-light pl-10 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                />
              </div>
            </div>

            {/* Conversations List */}
            <div className="flex flex-col overflow-y-auto flex-1 p-4 pt-0">
              {conversations
                .filter((conversation) => {
                  // If no search term, show all conversations
                  if (!searchTerm.trim()) return true;

                  // Filter by participant name (excluding current user)
                  const participantName = conversation.participants
                    ?.filter((p) => p && p.userId !== currentUser?.uid)
                    .map((p) => p.name.toLowerCase())
                    .join(" ");

                  return participantName?.includes(searchTerm.toLowerCase());
                })
                .map((conversation) => (
                  <div
                    key={conversation.id}
                    className={`p-3 md:p-4 flex items-center gap-3 md:gap-4 cursor-pointer hover:bg-base-200 active:bg-base-200 ${
                      selectedConversation?.id === conversation.id
                        ? "bg-base-200"
                        : ""
                    }`}
                    onClick={() => setSelectedConversation(conversation)}
                  >
                    {conversation.participants
                      ?.filter((p) => p && p.userId !== currentUser?.uid)
                      .map((p, i, arr) => (
                        <div
                          key={p.userId}
                          className={`w-10 h-10 min-w-10 relative rounded-full bg-base-content/50 flex items-center justify-center border-2 border-base-100 ${
                            i !== 0 ? "-ml-8" : ""
                          }`}
                          style={{ zIndex: arr.length - i }}
                        >
                          {p.imageUrlTiny || p.imageUrl ? (
                            <img
                              src={p.imageUrlTiny || p.imageUrl}
                              alt={p.name}
                              className="rounded-full object-cover w-full h-full"
                            />
                          ) : (
                            <span className="text-sm text-white">
                              {p.name.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                      ))}
                    <div className="flex flex-col gap-0.5 w-full">
                      <div className="flex items-center justify-between w-full">
                        <p className="font-medium">
                          {conversation.name ||
                            conversation.participants
                              ?.filter(
                                (p) => p && p.userId !== currentUser?.uid,
                              )
                              .map(
                                (p, i, arr) =>
                                  `${p.name}${i < arr.length - 1 ? ", " : ""}`,
                              )}
                        </p>
                        <p className="text-xs text-base-content/50">
                          {formatRelativeTime(
                            conversation.lastMessage?.timestamp,
                          )}
                        </p>
                      </div>
                      <p className="text-sm font-light text-base-content/60 line-clamp-1 text-ellipsis">
                        {conversation.lastMessage?.message
                          ? conversation.lastMessage.message
                          : "No messages yet."}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Main Content Area - Hidden on mobile when no conversation selected */}
          <div
            className={`flex-1 bg-base-100 md:rounded-md flex flex-col h-full overflow-hidden ${
              isMobile && !selectedConversation ? "hidden" : ""
            }`}
          >
            {selectedConversation ? (
              <div className="flex flex-col h-full overflow-hidden relative">
                {/* Conversation Header */}
                <div className="p-3 md:p-4 border-b w-full flex items-center justify-between flex-shrink-0 bg-base-300">
                  <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1 ">
                    {/* Back button - only on mobile */}
                    {isMobile && (
                      <button
                        onClick={handleBackToList}
                        className="p-2 hover:bg-base-200 rounded-full flex-shrink-0"
                      >
                        <ChevronLeftIcon className="w-6 h-6 text-base-content/80" />
                      </button>
                    )}
                    {selectedConversation.participants
                      ?.filter((p) => p && p.userId !== currentUser?.uid)
                      .map((p, i, arr) => (
                        <div
                          key={p.userId}
                          className={`w-10 h-10 md:w-12 md:h-12 min-w-10 md:min-w-12 relative rounded-full bg-base-content/50 flex items-center justify-center border-2 border-base-100 ${
                            i !== 0 ? "-ml-6 md:-ml-8" : ""
                          }`}
                          style={{ zIndex: arr.length - i }}
                        >
                          {p.imageUrlTiny || p.imageUrl ? (
                            <img
                              src={p.imageUrlTiny || p.imageUrl}
                              alt={p.name}
                              className="rounded-full object-cover w-full h-full"
                            />
                          ) : (
                            <span className="text-xs md:text-sm text-white">
                              {p.name.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                      ))}
                    <div className="flex flex-col min-w-0 flex-1">
                      <h2 className="text-lg md:text-xl font-heading truncate">
                        {selectedConversation.name ||
                          selectedConversation.participants
                            ?.filter((p) => p && p.userId !== currentUser?.uid)
                            .map(
                              (p, i, arr) =>
                                `${p.name}${i < arr.length - 1 ? ", " : ""}`,
                            )}
                      </h2>
                      {selectedConversation.name && (
                        <p className="text-xs text-base-content/60 truncate">
                          {selectedConversation.participants
                            ?.filter((p) => p && p.userId !== currentUser?.uid)
                            .map(
                              (p, i, arr) =>
                                `${p.name}${i < arr.length - 1 ? ", " : ""}`,
                            )}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2"></div>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-8 md:p-4 flex justify-center pb-32 md:pb-40">
                  <div className="flex flex-col gap-3 md:gap-4 w-full max-w-3xl h-fit px-1 md:px-2">
                    {messages.length === 0 ? (
                      <div className="flex absolute inset-0 pb-40 flex-col items-center justify-center">
                        <p className="text-base-content/50 font-light text-center">
                          No messages yet. Start the conversation!
                        </p>
                      </div>
                    ) : (
                      messages.map((message, index) => {
                        // Check if this message should be grouped with the previous one
                        const prevMessage =
                          index > 0 ? messages[index - 1] : null;
                        const isGrouped =
                          prevMessage &&
                          prevMessage.senderUid === message.senderUid &&
                          prevMessage.type !== "system" &&
                          message.type !== "system" &&
                          prevMessage.timestamp &&
                          message.timestamp &&
                          message.timestamp.toMillis() -
                            prevMessage.timestamp.toMillis() <
                            120000; // 2 minutes

                        // System messages
                        if (message.type === "system") {
                          return (
                            <div
                              key={message.id}
                              className="flex justify-center my-2"
                            >
                              <div className="text-xs text-base-content/60 bg-base-200 px-3 py-1 rounded-full">
                                {message.message}
                              </div>
                            </div>
                          );
                        }

                        // Attachment messages
                        if (
                          message.type === "attachment" &&
                          message.attachments &&
                          message.attachments.length > 0
                        ) {
                          const attachment = message.attachments[0];
                          const isImage = attachment.type?.startsWith("image/");

                          return (
                            <div
                              key={message.id}
                              className={`flex ${
                                message.senderUid === currentUser?.uid
                                  ? "justify-end"
                                  : "justify-start"
                              } ${isGrouped ? "-mt-2" : ""} group`}
                            >
                              <div className="flex flex-col gap-0.5 max-w-[85%] md:max-w-[75%] relative">
                                {!isGrouped && (
                                  <p
                                    className={`text-[11px] ${
                                      message.senderUid === currentUser?.uid
                                        ? "text-right text-base-content/50"
                                        : "text-left text-base-content/50"
                                    }`}
                                  >
                                    <span className="font-medium">
                                      {message.senderUid !== currentUser?.uid
                                        ? selectedConversation.participants?.find(
                                            (p) =>
                                              p.userId === message.senderUid,
                                          )?.name
                                        : "You"}
                                    </span>{" "}
                                    • {formatRelativeTime(message.timestamp)}
                                  </p>
                                )}
                                {message.senderUid === currentUser?.uid && (
                                  <button
                                    onClick={() =>
                                      handleDeleteClick(
                                        selectedConversation.id,
                                        message.id ?? "",
                                      )
                                    }
                                    className="absolute -left-8 md:-left-10 bottom-0 opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 md:w-8 md:h-8 border hover:bg-base-200 rounded-full flex items-center justify-center"
                                  >
                                    <TrashIcon className="w-3.5 h-3.5 md:w-4 md:h-4 text-red-500" />
                                  </button>
                                )}
                                <div
                                  className={`rounded-lg overflow-hidden ${
                                    message.senderUid === currentUser?.uid
                                      ? "bg-blue-500"
                                      : "bg-base-300"
                                  }`}
                                >
                                  {isImage ? (
                                    <a
                                      href={attachment.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block"
                                    >
                                      <img
                                        src={attachment.url}
                                        alt={attachment.name}
                                        className="max-w-xs md:max-w-sm max-h-96 object-contain"
                                      />
                                    </a>
                                  ) : (
                                    <a
                                      href={attachment.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`flex items-center gap-3 px-4 py-3 hover:opacity-80 transition-opacity ${
                                        message.senderUid === currentUser?.uid
                                          ? "text-white"
                                          : "text-base-content"
                                      }`}
                                    >
                                      <DocumentIcon className="w-8 h-8 flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">
                                          {attachment.name}
                                        </p>
                                        <p
                                          className={`text-xs ${
                                            message.senderUid ===
                                            currentUser?.uid
                                              ? "text-blue-100"
                                              : "text-base-content/60"
                                          }`}
                                        >
                                          {attachment.size
                                            ? `${(
                                                attachment.size / 1024
                                              ).toFixed(1)} KB`
                                            : "File"}
                                        </p>
                                      </div>
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        }

                        // Regular messages
                        return (
                          <div
                            key={message.id}
                            className={`flex ${
                              message.senderUid === currentUser?.uid
                                ? "justify-end"
                                : "justify-start"
                            } ${isGrouped ? "-mt-2" : ""} group`}
                          >
                            <div className="flex flex-col gap-0.5 max-w-[85%] md:max-w-[75%] relative">
                              {!isGrouped && (
                                <p
                                  className={`text-[11px] ${
                                    message.senderUid === currentUser?.uid
                                      ? "text-right text-base-content/50"
                                      : "text-left text-base-content/50"
                                  }`}
                                >
                                  <span className="font-medium">
                                    {message.senderUid !== currentUser?.uid
                                      ? selectedConversation.participants?.find(
                                          (p) => p.userId === message.senderUid,
                                        )?.name
                                      : "You"}
                                  </span>{" "}
                                  • {formatRelativeTime(message.timestamp)}
                                </p>
                              )}
                              {message.senderUid === currentUser?.uid && (
                                <button
                                  onClick={() =>
                                    handleDeleteClick(
                                      selectedConversation.id,
                                      message.id ?? "",
                                    )
                                  }
                                  className="absolute -left-8 md:-left-10 bottom-0 opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 md:w-8 md:h-8 border hover:bg-base-200 rounded-full flex items-center justify-center"
                                >
                                  <TrashIcon className="w-3.5 h-3.5 md:w-4 md:h-4 text-red-500" />
                                </button>
                              )}
                              <div
                                className={`w-full rounded-lg px-3 py-2 md:px-4 md:py-2 ${
                                  message.senderUid === currentUser?.uid
                                    ? "bg-blue-500 text-white rounded-br-none"
                                    : "bg-base-300 text-base-content"
                                }`}
                              >
                                <p className="text-sm md:text-base">
                                  {message.message}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {/* Message Input Area */}
                <div className="absolute bottom-0 w-full flex flex-col items-center justify-center flex-shrink-0 px-4">
                  <div className="w-full max-w-4xl h-6 md:h-8 bg-gradient-to-t from-base-100 to-transparent"></div>
                  <div className="flex w-full items-end max-w-4xl bg-base-100 px-2 md:px-0">
                    <div className="w-full flex flex-col bg-base-300 items-end gap-1 border border-base-300 shadow-sm rounded-xl p-2 md:p-3 focus-within:none focus-within:none focus-within:none transition-all">
                      <textarea
                        rows={2}
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        placeholder="Type your message..."
                        className="w-full resize-none border-none bg-transparent focus:outline-none placeholder-base-content/50 text-base font-light"
                        autoFocus={!isMobile}
                      />
                      <div className="w-full justify-between flex items-center">
                        <button
                          onClick={() => setShowAttachmentModal(true)}
                          disabled={sendingMessage}
                          className="p-1.5 md:p-2 bg-neutral/70 rounded-lg text-base-content/50 hover:text-base-content/70 disabled:opacity-10 transition-colors flex-shrink-0"
                        >
                          <PaperClipIcon className="w-4 h-4 md:w-5 md:h-5 text-white" />
                        </button>
                        <button
                          onClick={handleSendMessage}
                          disabled={!messageInput.trim() || sendingMessage}
                          className="p-1.5 md:p-2 bg-neutral rounded-lg text-base-content/50 hover:text-base-content/70 disabled:opacity-10 transition-colors flex-shrink-0"
                        >
                          <PaperAirplaneIcon className="w-4 h-4 md:w-5 md:h-5 text-white" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="w-full max-w-4xl h-6 md:h-8 pb-20 md:pb-0 bg-base-100"></div>
                </div>
              </div>
            ) : (
              <div className="p-4">
                <p className="text-base-content/60">
                  Select a conversation to view messages.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Attachment Modal */}
      {showAttachmentModal && (
        <AttachmentModal
          onClose={() => setShowAttachmentModal(false)}
          onSend={handleSendAttachment}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-base-100 rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-2">Delete Message</h3>
            <p className="text-base-content/70 mb-6">
              Are you sure you want to delete this message? This action cannot
              be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDeleteMessage}
                className="px-4 py-2 text-base-content/80 bg-base-200 hover:bg-base-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteMessage}
                className="px-4 py-2 text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar - Hidden on mobile when conversation is selected */}
      {!(isMobile && selectedConversation) && <BottomNavBar />}
    </div>
  );
};

export default InboxPage;
